import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, signal, WritableSignal, computed, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AppFacadeService } from '../../services/app-facade.service';
import { Consumer } from '../../admin/models/consumer-model';
import { ColDef, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { ContactResponse } from '../../admin/interfaces/find-email.interface';
import { FormatDatePipe } from '../../pipes/format-date.pipe';
import { formatDateType } from '../../types';
import { Filter, FilterOutput } from '../../shared/models/filters.model';
import { ContactFilterStatus } from '../../enums/contact-filter-status.enum';
import { PermissionCheckboxRendererComponent } from './renderer/permission-checkbox.component';
import { ContactActionsRendererComponent } from './renderer/contact-actions.component';
import { AssignUserPermissionRequest } from '../../admin/interfaces/user-accout.interface';
import { AgGridCustomHeaderComponent } from '../../shared/components/ag-grid-header/ag-grid-header.component';
import { Customer } from '../../models/consumer';
import { AgGridService } from '../../services/ag-grid.service';
import { ModalService } from '../../services/modal.service';
import { DEFAULT_PERMISSIONS_MODAL_ID, DefaultPermissionsModalComponent } from './default-permissions-modal/default-permissions-modal.component';
import { UserAccountService } from '../../services/user-account.service';
import { UserAccountFeature } from '../../enums/user-account-feature.enum';
import { NotificationService } from '../../services/notification.service';
import { ADD_CONTACT_MODAL_ID } from './add-contact-modal/add-contact-modal.component';
import { Subscription } from 'rxjs';

@Component({
    standalone: false,
    selector: 'por-contacts',
    templateUrl: './contacts.component.html',
    styleUrls: ['./contacts.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContactsComponent implements OnInit, OnDestroy {
    private readonly translateService = inject(TranslateService);
    readonly appFacadeService = inject(AppFacadeService);
    private readonly datePipe = inject(FormatDatePipe);
    private readonly agGridService = inject(AgGridService);
    private readonly modalService = inject(ModalService);
    private readonly userAccountService = inject(UserAccountService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly notificationService = inject(NotificationService);
    @Input() consumer!: Consumer | Customer | null;
    @Input() set consumerContacts(data: ContactResponse[]) {
        this.allContacts.set(data || []);
    }

    @Output() readonly closePanel = new EventEmitter<boolean>(false);

    @ViewChild(DefaultPermissionsModalComponent) defaultPermissionsModal?: DefaultPermissionsModalComponent;

    columnDefs: ColDef[] = [];
    defaultColDef: ColDef = {
        sortable: true,
        suppressMovable: true,
        resizable: false,
        wrapText: true,
        sortingOrder: ['asc', 'desc'],
        suppressMenu: true,
        flex: 1
    };

    gridOptions: GridOptions = {
        suppressContextMenu: true,
        frameworkComponents: {
            permissionCheckboxRenderer: PermissionCheckboxRendererComponent,
            contactActionsRenderer: ContactActionsRendererComponent
        }
    };

    public readonly context: { componentParent: ContactsComponent };

    private gridApi!: GridApi;
    private contactsLoading = false;
    private permissionsLoading = true;
    private readonly subscriptions: Subscription[] = [];

    // Signals for data and filter state
    readonly allContacts: WritableSignal<ContactResponse[]> = signal([]);
    readonly activeFilters: WritableSignal<string[]> = signal([ContactFilterStatus.Active]);
    readonly initFilters: string[] = [ContactFilterStatus.Active];
    readonly filteredContacts = computed(() => {
        const contacts = this.allContacts();
        const filters = this.activeFilters();

        if (filters.length === 0) {
            return contacts;
        }

        return contacts.filter(contact => {
            const includeIfRegistered = !filters.includes(ContactFilterStatus.IsRegisterOnPortal) || contact.isRegisterOnPortal;

            const includeIfActive = !filters.includes(ContactFilterStatus.Active) || contact.isActive;

            return includeIfRegistered && includeIfActive;
        });
    });

    filterValues: Filter[] = [];

    visibleFeatures: string[] = [];

    constructor() {
        this.context = {
            componentParent: this
        };
    }

    ngOnInit(): void {
        this.filterValues = [
            {
                label: this.translateService.instant('RegisteredOnPortal'),
                value: ContactFilterStatus.IsRegisterOnPortal
            },
            {
                label: this.translateService.instant('Active'),
                value: ContactFilterStatus.Active
            }
        ];

        this.buildColumnDefs();

        this.userAccountService.getVisibleFeatures().subscribe(features => {
            this.visibleFeatures = features.map(f => f.featureName);
            this.permissionsLoading = false;
            this.updateLoadingOverlay(this.permissionsLoading || this.contactsLoading);
            this.buildColumnDefs();
            this.cdr.markForCheck();
        });
    }

    private buildColumnDefs(): void {
        this.columnDefs = [
            {
                field: 'name',
                headerName: this.translateService.instant('UserName'),
                cellClass: 'theme-table-row',
                headerClass: 'theme-table-header',
                minWidth: 155,
                wrapText: true
            },
            {
                field: 'email',
                headerName: this.translateService.instant('Email'),
                cellRenderer: (params: { value: string; data: ContactResponse }) => {
                    const email = params.value ?? '';
                    if (params.data?.isRegisterOnPortal) {
                        const label = this.translateService.instant('Registered');
                        return `${email} <span class="registered-tag">${label}</span>`;
                    }
                    return email;
                },
                cellClass: 'theme-table-row',
                headerClass: 'theme-table-header',
                minWidth: 250,
                resizable: true,
                wrapText: true
            },
            {
                field: 'lastLoginDateTime',
                headerName: this.translateService.instant('LastLogin'),
                cellRenderer: this.formatDate,
                cellClass: 'theme-table-row',
                headerClass: 'theme-table-header',
                wrapText: true
            },
            ...this.visibleFeatures.map(feature => ({
                headerName: this.translateService.instant(`${feature}`),
                headerComponent: AgGridCustomHeaderComponent,
                headerComponentParams: {
                    isShowTooltip: true,
                    tooltipTitle: this.translateService.instant(`${feature}.TooltipTitle`),
                    tooltipContent: this.translateService.instant(`${feature}.TooltipContent`)
                },
                field: 'permissions',
                cellRenderer: 'permissionCheckboxRenderer',
                cellRendererParams: {
                    permissionKey: `${feature}`
                },
                wrapText: true,
                editable: false
            })),
            {
                headerName: this.translateService.instant('actions'),
                cellRenderer: 'contactActionsRenderer',
                cellClass: 'theme-table-row',
                headerClass: 'theme-table-header'
            }
        ];

        this.subscriptions.push(
            this.appFacadeService.getCustomerContactsLoading().subscribe(loading => {
                this.contactsLoading = loading;
                this.updateLoadingOverlay(this.permissionsLoading || this.contactsLoading);
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private updateLoadingOverlay(loading: boolean): void {
        if (!this.gridApi) {
            return;
        }
        if (loading) {
            this.gridApi.showLoadingOverlay();
        } else {
            this.gridApi.hideOverlay();
        }
    }

    private readonly formatDate = (params: formatDateType) => {
        const date = this.datePipe.transform(params.value, this.appFacadeService.featureToggleService.isSyrinx());
        return `<span class="theme-table-row">${date}</span>`;
    };

    onGridReady(params: GridReadyEvent<unknown>) {
        this.gridApi = params.api;
        this.gridApi.setDomLayout('autoHeight');
        params.columnApi?.autoSizeAllColumns();
        this.agGridService.setHorizontalScrollBar();
        this.updateLoadingOverlay(this.permissionsLoading || this.contactsLoading);
    }

    onCloseButtonClick() {
        this.closePanel.emit(true);
    }

    private validateMinimumRequiredPermissions(): boolean {
        return this.allContacts().filter(contact => !!contact.permissions?.[UserAccountFeature.ManageUsers]).length > 1;
    }

    filter($e: FilterOutput) {
        const filters = $e.multifilters || [];
        this.activeFilters.set(filters);
    }

    get customerId(): string {
        return this.consumer?.Id ?? '';
    }

    onCheckboxChange(data: AssignUserPermissionRequest): void {
        if (data.permission === UserAccountFeature.ManageUsers && !data.enabled && !this.validateMinimumRequiredPermissions()) {
            this.notificationService.error(this.translateService.instant('DefaultPermissions.MinimumPermissionViolationWarning'));
            this.gridApi.refreshCells({ force: true });
            return;
        }
        this.appFacadeService.assignOrRevokePermissions(data);
    }

    private countActiveManageUsers(): number {
        // Only active users with Manage Users count toward the minimum — an inactive user isn't a valid manager
        return this.allContacts().filter(contact => contact.isActive && !!contact.permissions?.[UserAccountFeature.ManageUsers]).length;
    }

    onDeactivate(contact: ContactResponse): void {
        const canManageUsers = !!contact.permissions?.[UserAccountFeature.ManageUsers];
        if (canManageUsers && this.countActiveManageUsers() <= 1) {
            this.notificationService.error(this.translateService.instant('DefaultPermissions.MinimumPermissionViolationWarning'));
            return;
        }
        this.appFacadeService.setContactActiveStatus(this.customerId, contact.contactId!, false);
    }

    onActivate(contact: ContactResponse): void {
        this.appFacadeService.setContactActiveStatus(this.customerId, contact.contactId!, true);
    }

    quickFilterText = '';

    onQuickFilterChange(value: string): void {
        this.quickFilterText = value ?? '';
    }

    openDefaultPermissionsModal(): void {
        this.defaultPermissionsModal?.load();
        this.modalService.open(DEFAULT_PERMISSIONS_MODAL_ID);
    }

    onAddContactClick($event: MouseEvent): void {
        this.modalService.open(ADD_CONTACT_MODAL_ID);
    }
}
